/**
 * Shop The Look — Cart Drawer Upsell (LAZY FETCH)
 * -------------------------------------------------
 * On cart change:
 *   1. Reads the lookup table (handle -> product IDs)
 *   2. Finds the most-recent cart item that matches a look
 *   3. Fetches that look's products via Shopify ajax
 *   4. Renders the upsell cards
 *
 * Caches fetched looks in memory so we don't refetch.
 */

(function () {
  'use strict';

  const POOL_SELECTOR = '[data-stl-pool]';
  const LOOKUP_SELECTOR = '[data-stl-lookup]';
  const PRODUCTS_CONTAINER = '[data-stl-products-container]';
  const TITLE_SELECTOR = '[data-stl-look-title]';
  const CART_DRAWER_SELECTOR = '#cart-drawer';

  let lookupCache = null;
  const renderedLookCache = {};

  function getLookup() {
  if (lookupCache) return lookupCache;
  const node = document.querySelector(LOOKUP_SELECTOR);
  if (!node) return null;
  try {
    lookupCache = JSON.parse(node.textContent);
    return lookupCache;
  } catch (e) {
    console.error('STL: failed to parse lookup table', e);
    return null;
  }
}

  function getCartProductIds() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        if (!cart || !cart.items) return [];
        return cart.items.slice().reverse().map((i) => i.product_id);
      })
      .catch(() => []);
  }

  function findMatchingLook(lookup, cartProductIds) {
    if (!lookup || !lookup.looks || !cartProductIds.length) return null;
    for (let i = 0; i < cartProductIds.length; i++) {
      const pid = cartProductIds[i];
      for (let j = 0; j < lookup.looks.length; j++) {
        const look = lookup.looks[j];
        if (look.products.indexOf(pid) !== -1) {
          return look;
        }
      }
    }
    return null;
  }

  function fetchLookHTML(handle, maxItems, excludeIds) {
    const cacheKey = handle + ':' + excludeIds.join(',');
    if (renderedLookCache[cacheKey]) {
      return Promise.resolve(renderedLookCache[cacheKey]);
    }
    return fetch('/collections/' + handle + '/products.json?limit=' + (maxItems + 5))
      .then((r) => r.json())
      .then((data) => {
        const products = (data.products || [])
          .filter((p) => excludeIds.indexOf(p.id) === -1)
          .slice(0, maxItems);
        const html = products.map(renderProductCard).join('');
        renderedLookCache[cacheKey] = html;
        return html;
      })
      .catch((e) => {
        console.error('STL: fetch failed', e);
        return '';
      });
  }

function renderProductCard(product) {
  const image = product.images && product.images[0]
    ? product.images[0].src
    : '';
  const variant = product.variants && product.variants[0];
  const price = variant ? formatMoney(variant.price * 100) : '';
  const url = '/products/' + product.handle;
  const variantId = variant ? variant.id : '';
  const available = product.variants && product.variants.some((v) => v.available);
  const hasMultipleVariants = product.variants && product.variants.length > 1;

  let actionHtml = '';
  if (!available) {
    actionHtml = '<span class="stl-product-card__unavail">Sold out</span>';
  } else if (hasMultipleVariants) {
    // Multiple variants — link to product page so customer picks size/color
    actionHtml = '<a href="' + url + '" class="btn btn--small btn--primary stl-product-card__add">Choose options</a>';
  } else {
    // Single variant — direct add to cart
    actionHtml = renderAddButton(variantId);
  }

  return (
    '<div class="stl-product-card upsell-product" data-stl-product-id="' + product.id + '">' +
      '<a href="' + url + '" class="stl-product-card__image-link">' +
        (image ? '<img src="' + image + '" alt="' + escapeHtml(product.title) + '" loading="lazy" class="stl-product-card__image" />' : '') +
      '</a>' +
      '<div class="stl-product-card__info">' +
        '<a href="' + url + '" class="stl-product-card__title">' + escapeHtml(product.title) + '</a>' +
        '<div class="stl-product-card__price">' + price + '</div>' +
        actionHtml +
      '</div>' +
    '</div>'
  );
}

  function renderAddButton(variantId) {
    return (
      '<form action="/cart/add" method="post" enctype="multipart/form-data" class="stl-product-card__form">' +
        '<input type="hidden" name="id" value="' + variantId + '" />' +
        '<button type="submit" class="btn btn--small btn--primary stl-product-card__add" data-add-to-cart>Add</button>' +
      '</form>'
    );
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.moneyFormat && window.themeVendor && window.themeVendor.themeCurrency) {
      try {
        return window.themeVendor.themeCurrency.formatMoney(cents, window.theme.moneyFormat);
      } catch (e) {
        // fall through
      }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

function update() {
  const pools = document.querySelectorAll(POOL_SELECTOR);
  if (!pools.length) return;

  const lookup = getLookup();
  if (!lookup) return;

  getCartProductIds().then((cartIds) => {
    const match = findMatchingLook(lookup, cartIds);

    pools.forEach((pool) => {
      const maxItems = parseInt(pool.getAttribute('data-stl-max-items') || '3', 10);
      const titleEl = pool.querySelector(TITLE_SELECTOR);
      const productsContainer = pool.querySelector(PRODUCTS_CONTAINER);

      if (!match) {
        pool.setAttribute('hidden', '');
        return;
      }
      if (titleEl) titleEl.textContent = match.title;

      fetchLookHTML(match.handle, maxItems, cartIds).then((html) => {
        if (!html) {
          pool.setAttribute('hidden', '');
          return;
        }
        productsContainer.innerHTML = html;
        pool.removeAttribute('hidden');
      });
    });
  });
}

  function init() {
    update();
    document.addEventListener('theme:cart:change', update);
    document.addEventListener('theme:cart-drawer:open', update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();